import { useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

import { Draggable, DraggableStateSnapshot } from '@hello-pangea/dnd';
import type {
  DraggableProvidedDraggableProps,
  DraggableProvidedDragHandleProps,
} from '@hello-pangea/dnd';
import { Box, SxProps, Theme } from '@mui/material';

import { SignatureOrderType, SignatureOrderUser, SignerData } from '../../types/taskForm';
import { DragAndDropSignerBoxContentAndActions } from './DragAndDropSignerBoxContentAndActions';
import { getDraggableId } from './utils';

type SignerBoxStyle = 'overflowed' | 'normal';

interface DragAndDropSignerBoxProps {
  signer: SignatureOrderUser[];
  index: number;
  usersData: Record<string, SignerData>;
  onSignerDelete: (signerId: string | number) => void;
  orderType: SignatureOrderType;
  isDragDisabled?: boolean;
  isIncomplete?: boolean;
  signers?: SignatureOrderUser[];
  handleUnGroup?: (signerId: string, groupIndex: number) => void;
  snapshot?: DraggableStateSnapshot;
}

const customStyles: Record<string, SxProps<Theme>> = {
  signerBox: {
    padding: '7px 10px',
    borderRadius: '8px',
    cursor: 'grab',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '40px',
    boxSizing: 'border-box',
    backgroundColor: (theme) => theme.palette.common.white,
    border: (theme: Theme) => `1px solid ${theme.palette.divider}`,
    boxShadow: 'none',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
  },
  overflowedSignerPadding: {
    paddingTop: '28px',
    paddingBottom: '28px',
  },
  normalSignerPadding: {
    paddingTop: '16px',
    paddingBottom: '16px',
  },
  draggingCard: {
    backgroundColor: (theme) => theme.palette.action.hover,
    border: (theme: Theme) => `1.5px solid ${theme.palette.primary.main}`,
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
  },
  warningCard: {
    border: (theme) => `1px solid ${theme.palette.warning.main}`,
  },
};

interface DragAndDropSignerBoxContainerProps {
  boxContent: SignerBoxStyle;
  isDragging?: boolean;
  isIncomplete?: boolean;
  innerRef?: (element: HTMLElement | null) => void;
  draggableProps?: DraggableProvidedDraggableProps;
  dragHandleProps?: DraggableProvidedDragHandleProps | null;
  extraStyle?: CSSProperties;
  children: ReactNode;
}

function DragAndDropSignerBoxContainer({
  boxContent,
  isDragging = false,
  isIncomplete = false,
  innerRef,
  draggableProps,
  dragHandleProps,
  extraStyle,
  children,
}: DragAndDropSignerBoxContainerProps) {
  const paddingSx =
    boxContent === 'overflowed'
      ? customStyles.overflowedSignerPadding
      : customStyles.normalSignerPadding;
  return (
    <Box
      ref={innerRef}
      {...(draggableProps ?? {})}
      {...(dragHandleProps ?? {})}
      style={extraStyle}
      sx={
        {
          ...customStyles.signerBox,
          ...(isDragging ? customStyles.draggingCard : {}),
          ...(isIncomplete ? customStyles.warningCard : {}),
          ...paddingSx,
        } as SxProps<Theme>
      }
    >
      {children}
    </Box>
  );
}

export function DragAndDropSignerBox({
  signer,
  index,
  usersData,
  onSignerDelete,
  orderType,
  isDragDisabled = false,
  isIncomplete = false,
  signers = [],
  handleUnGroup,
  snapshot,
}: DragAndDropSignerBoxProps) {
  const [isOverflowed, setIsOverflowed] = useState(false);

  const isSimultaneous = orderType === SignatureOrderType.SIMULTANEOUS;
  const actualSigner = signer[0];
  const signerId: string = getDraggableId(actualSigner);

  if (isSimultaneous) {
    return (
      <Draggable
        key={signerId}
        draggableId={signerId}
        index={index}
        isDragDisabled={isDragDisabled}
      >
        {(provided, dragSnapshot) => (
          <DragAndDropSignerBoxContainer
            innerRef={provided.innerRef}
            draggableProps={provided.draggableProps}
            dragHandleProps={provided.dragHandleProps}
            extraStyle={provided.draggableProps.style}
            boxContent={isOverflowed ? 'overflowed' : 'normal'}
            isDragging={dragSnapshot.isDragging}
            isIncomplete={isIncomplete}
          >
            <DragAndDropSignerBoxContentAndActions
              orderType={orderType}
              isDragDisabled={isDragDisabled}
              index={index}
              signers={signers}
              signer={signer}
              usersData={usersData}
              onSignerDelete={onSignerDelete}
              handleUnGroup={handleUnGroup}
              onOverflowChange={setIsOverflowed}
            />
          </DragAndDropSignerBoxContainer>
        )}
      </Draggable>
    );
  }

  return (
    <DragAndDropSignerBoxContainer
      boxContent={isOverflowed ? 'overflowed' : 'normal'}
      isDragging={Boolean(snapshot?.isDragging)}
      isIncomplete={isIncomplete}
    >
      <DragAndDropSignerBoxContentAndActions
        orderType={orderType}
        isDragDisabled={isDragDisabled}
        index={index}
        signers={signers}
        signer={signer}
        usersData={usersData}
        onSignerDelete={onSignerDelete}
        handleUnGroup={handleUnGroup}
        onOverflowChange={setIsOverflowed}
      />
    </DragAndDropSignerBoxContainer>
  );
}
